"use client";

import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TextField,
  TablePagination,
  Chip,
  IconButton,
  InputAdornment,
  Card,
  useTheme,
  alpha,
  Skeleton, // ✅ added Skeleton
} from "@mui/material";
import {
  Search as SearchIcon,
  Key as KeyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { debounce } from "lodash";

function Key() {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApiKey, setShowApiKey] = useState({});
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalUsers, setTotalUsers] = useState(0);

  const fetchApiKeys = async (searchTerm = "", pageNumber = 1, limit = 5) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/apikey/admin/get-all-api-keys`, {
        params: { email: searchTerm, page: pageNumber, limit },
      });

      if (response.data.success) {
        setUsers(response.data.users);
        setTotalUsers(response.data.totalUsers);
      }
    } catch (error) {
      console.error("Error fetching API keys:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setPage(0);
        fetchApiKeys(value, 1, rowsPerPage);
      }, 500),
    [rowsPerPage]
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchApiKeys(search, newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    fetchApiKeys(search, 1, newLimit);
  };

  const toggleApiKeyVisibility = (id) => {
    setShowApiKey((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleRefresh = () => {
    fetchApiKeys(search, page + 1, rowsPerPage);
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  // Flatten users for table
  const flattenedUsers = useMemo(() => {
    return users.flatMap((user) =>
      user.api_keys.map((key) => ({
        ...key,
        username: user.username,
        email: user.email,
        userId: user.id,
      }))
    );
  }, [users]);

  const getStatusChip = (status) => {
    let color;
    switch (status) {
      case "active":
        color = "success";
        break;
      case "expired":
        color = "error";
        break;
      case "revoked":
        color = "default";
        break;
      default:
        color = "warning";
    }
    return <Chip label={status} color={color} size="small" />;
  };

  return (
    <Box
      sx={{
        padding: 4,
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.05
        )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <KeyIcon
          sx={{
            fontSize: 36,
            color: theme.palette.primary.main,
            mr: 2,
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
          }}
        >
          API Keys Management
        </Typography>
        <IconButton
          onClick={handleRefresh}
          sx={{
            ml: "auto",
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
            },
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Search */}
      <TextField
        placeholder="Search by email..."
        variant="outlined"
        value={search}
        onChange={handleSearchChange}
        sx={{
          marginBottom: 3,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            backgroundColor: "white",
            "&:hover fieldset": {
              borderColor: theme.palette.primary.main,
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="primary" />
            </InputAdornment>
          ),
        }}
      />

      {/* Table */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.05)",
          background: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(10px)",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#1976d2",
                  "& th, & td": {
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    py: 2,
                    borderBottom: (theme) =>
                      `2px solid ${theme.palette.primary.main}33`,
                  },
                }}
              >
                <TableCell>User ID</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>API Key</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expires At</TableCell>
                <TableCell>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // ✅ Skeleton loader while fetching
                [...Array(rowsPerPage)].map((_, i) => (
                  <TableRow key={i}>
                    {Array(7)
                      .fill("")
                      .map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton
                            variant="rectangular"
                            height={28}
                            animation="wave"
                            sx={{ bgcolor: "white", borderRadius: 1 }}
                          />
                        </TableCell>
                      ))}
                  </TableRow>
                ))
              ) : flattenedUsers.length > 0 ? (
                flattenedUsers.map((key) => (
                  <TableRow
                    key={key.id}
                    hover
                    sx={{
                      "&:nth-of-type(even)": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                      },
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {key.userId}
                    </TableCell>
                    <TableCell>{key.username}</TableCell>
                    <TableCell>{key.email}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {showApiKey[key.id]
                          ? key.api_key
                          : "•".repeat(20)}
                        <IconButton
                          size="small"
                          onClick={() => toggleApiKeyVisibility(key.id)}
                          sx={{ ml: 1 }}
                        >
                          {showApiKey[key.id] ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>{getStatusChip(key.status)}</TableCell>
                    <TableCell>
                      {new Date(key.expires_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {new Date(key.createdAt).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <KeyIcon
                      sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No API keys found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search
                        ? "Try a different search term"
                        : "No API keys available in the system"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalUsers}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontWeight: 500,
              },
          }}
        />
      </Card>
    </Box>
  );
}

export default Key;
